// src/Services/AdminService/AdminCourseManagementService.ts
import httpClient from '@/services/httpClient';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Standard API response wrapper from backend
 * All API endpoints return this structure: { success, data, message }
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Course creation/update request payload
 */
export interface CoursePayload {
  id?: number;
  creator_id?: string;
  title: string;
  instructor?: string;
  shortDescription?: string;
  description?: string;
  languageId: number;
  categoryId: number;
  subCategoryId: number;
  level: 'beginner' | 'intermediate' | 'advanced';
  courseDuration?: string;
  sections?: Section[];
  thumbnail?: string | File;
  mediaType?: string;
  mediaUrl?: string | File;
  metaKeywords?: string[];
  metaDescription?: string;
  outcomes?: string[];
  requirements?: string[];
  faqs?: FAQ[];
  lessons?: Lesson[];
  batchIds?: number[];
  availableToAllBatches?: boolean;
}

/**
 * Course section
 */
export interface Section {
  id?: number;
  title: string;
  sectionOrder?: number;
  createdAt?: string;
  lessons?: Lesson[];
}

/**
 * Course lesson
 */
export interface Lesson {
  id?: number | string;
  title: string;
  section: string;
  sectionId?: number;
  lessonType: 'ILTS' | 'Content-Based';
  skills?: string[];

  // Content-Based fields
  contentType?: 'document' | 'scorm' | 'mp4' | 'url' | 'quiz';
  lessonContentDocument?: string | File;
  scormPackage?: string | File;
  videoUpload?: string | File;
  contentUrl?: string;
  lessonDuration?: string;
  description?: string;

  // Quiz/Assessment fields
  assessmentId?: number;
  requireSectionCompletion?: boolean;
  assessmentStartDate?: string;
  assessmentEndDate?: string;

  // ILTS fields
  iltsType?: 'Online' | 'Offline';
  iltsUrl?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  eventVenue?: string;
  meetUrl?: string;
}

/**
 * Course FAQ
 */
export interface FAQ {
  question: string;
  answer: string;
}

/**
 * Course metadata (categories, languages)
 */
export interface Category {
  id: number;
  name: string;
  description?: string;
}

export interface SubCategory {
  id: number;
  name: string;
  categoryId: number;
}

export interface Language {
  id: number;
  name: string;
  code: string;
}

export interface Instructor {
  userId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  profileImage?: string;
}

export interface Batch {
  id: number;
  batch_name: string;
}

/**
 * Course basic data from API
 */
export interface CourseData {
  id: number;
  title: string;
  shortDescription?: string;
  description?: string;
  languageId: number;
  categoryId: number;
  subCategoryId: number;
  level: string;
  courseDuration?: string;
  thumbnail?: string;
  status: string;
  metaKeywords?: string;
  metaDescription?: string;
  createdAt: string;
  updatedAt: string;
  creatorId?: string;
  instructorName?: string;
  instructorDp?: string;
}

/**
 * Course details response (nested structure from API)
 */
export interface CourseDetails {
  course: CourseData;
  outcomes?: Array<{ id: number; outcome: string; outcomeText?: string }>;
  requirements?: Array<{ id: number; requirement: string; requirementText?: string }>;
  faqs?: Array<{ id: number; question: string; answer: string }>;
  sections?: Section[];
  lessons?: Lesson[];
  ratings?: any[];
  enrollments?: any[];
  lessonSkills?: any[];
  ilts?: any[];
  batches?: any[];
  batchIds?: number[];
  availableToAllBatches?: boolean;
}

/**
 * Course list item
 */
export interface CourseListItem {
  id: number;
  title: string;
  shortDescription?: string;
  thumbnail?: string;
  level: string;
  status: string;
  enrollmentCount: number;
  lessonCount: number;
  avgRating?: number;
  totalRatings?: number;
  avgCompletion?: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Course stats for admin dashboard cards
 */
export interface CourseStats {
  activeCourses: number;
  totalLearners: number;
  avgRating: number;
  totalRatings: number;
  avgCompletion: number;
}

/**
 * Lesson Details - Complete lesson data from backend
 */
export interface LessonData {
  id: number;
  title: string;
  lessonType: 'Content-Based' | 'ILTS';
  contentType?: 'document' | 'mp4' | 'scorm' | 'url';
  duration?: string;
  description?: string;

  // Content-Based fields
  lessonContentDocument?: string;
  lesson_content_mp4?: string;
  lesson_content_scorm?: string;
  lesson_content_url?: string;

  // ILTS fields
  iltsType?: 'Online' | 'Offline';
  iltsUrl?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  eventVenue?: string;
  meetUrl?: string;
}

// ============================================================================
// API ENDPOINTS
// ============================================================================

const ENDPOINTS = {
  // Course CRUD (using admin endpoints)
  CREATE_COURSE: '/admin/courses',
  UPDATE_COURSE: (courseId: number) => `/admin/courses/${courseId}`,
  DELETE_COURSE: (courseId: number) => `/admin/courses/${courseId}`,
  GET_COURSE_DETAILS: (courseId: number) => `/admin/courses/${courseId}`,

  // Course updates (specific sections)
  UPDATE_BASIC_INFO: (courseId: number) => `/admin/courses/${courseId}/basic`,
  UPDATE_DETAILS: (courseId: number) => `/admin/courses/${courseId}/details`,
  UPDATE_MEDIA: (courseId: number) => `/admin/courses/${courseId}/media`,
  UPDATE_META: (courseId: number) => `/admin/courses/${courseId}/meta`,

  // Sections and Lessons
  ADD_SECTION: (courseId: number) => `/admin/courses/${courseId}/sections`,
  GET_SECTIONS: (courseId: number) => `/admin/courses/${courseId}/sections`,
  ADD_LESSON: (courseId: number) => `/admin/courses/${courseId}/lessons`,
  UPDATE_LESSON: (courseId: number, lessonId: number) => `/admin/courses/${courseId}/lessons/${lessonId}`,
  DELETE_LESSON: (courseId: number, lessonId: number) => `/admin/courses/${courseId}/lessons/${lessonId}`,
  GET_LESSON_DETAILS: (courseId: number, lessonId: number) => `/admin/courses/${courseId}/lessons/${lessonId}`,

  REORDER_LESSONS: (courseId: number) => `/admin/courses/${courseId}/lessons/reorder`,

  // Outcomes, Requirements, FAQs
  UPDATE_OUTCOMES: (courseId: number) => `/admin/courses/${courseId}/outcomes`,
  UPDATE_REQUIREMENTS: (courseId: number) => `/admin/courses/${courseId}/requirements`,
  UPDATE_FAQS: (courseId: number) => `/admin/courses/${courseId}/faqs`,

  // Course lists (Admin-specific endpoints)
  GET_ACTIVE_COURSES: '/admin/courses/admin/active',
  GET_PENDING_COURSES: '/admin/courses/admin/pending',
  GET_ALL_COURSES: '/admin/courses/admin/all',
  GET_COURSE_STATS: '/admin/courses/admin/stats',

  // Metadata
  GET_CATEGORIES: '/admin/courses/metadata/categories',
  GET_SUBCATEGORIES: '/admin/courses/metadata/subcategories',
  GET_LANGUAGES: '/admin/courses/metadata/languages',
  GET_INSTRUCTORS: '/admin/courses/metadata/instructors',
  ADD_CATEGORY: '/admin/courses/metadata/categories',
  ADD_SUBCATEGORY: '/admin/courses/metadata/subcategories',

  // Enrollments and Analytics
  GET_ENROLLED_STUDENTS: (courseId: number) => `/admin/courses/${courseId}/students`,
  GET_STUDENT_PROGRESS: (courseId: number) => `/admin/courses/${courseId}/enrollments/progress`,
  GET_COURSE_ANALYTICS: (courseId: number) => `/admin/courses/${courseId}/analytics`,
  GET_COURSE_BATCHES: (courseId: number) => `/admin/courses/${courseId}/batches`,

  // Batches
  GET_BATCHES: '/admin/batches',

  // Admin-specific operations
  APPROVE_COURSE: (courseId: number) => `/admin/courses/${courseId}/approve`,
  REJECT_COURSE: (courseId: number) => `/admin/courses/${courseId}/reject`,
  UPDATE_COURSE_STATUS: (courseId: number) => `/admin/courses/${courseId}/status`,
};

// ============================================================================
// SERVICE FUNCTIONS - Course CRUD
// ============================================================================

/**
 * Create a new course
 * @param courseData - Course creation data
 */
export const createCourse = async (courseData: CoursePayload): Promise<{
  success: boolean;
  data?: { courseId: number; id: number };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ courseId: number; id: number }>>(
      ENDPOINTS.CREATE_COURSE,
      courseData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to create course',
      },
    };
  }
};

/**
 * Update an existing course
 * @param courseId - ID of the course to update
 * @param courseData - Updated course data
 */
export const updateCourse = async (
  courseId: number,
  courseData: Partial<CoursePayload>
): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<any>>(
      ENDPOINTS.UPDATE_COURSE(courseId),
      courseData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update course',
      },
    };
  }
};

/**
 * Delete a course (soft delete)
 * @param courseId - ID of the course to delete
 */
export const deleteCourse = async (courseId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(ENDPOINTS.DELETE_COURSE(courseId));

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete course',
      },
    };
  }
};

/**
 * Get course details by ID
 * @param courseId - ID of the course
 */
export const getCourseDetails = async (courseId: number): Promise<{
  success: boolean;
  data?: CourseDetails;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CourseDetails>>(
      ENDPOINTS.GET_COURSE_DETAILS(courseId)
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch course details',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Course Updates (Specific Sections)
// ============================================================================

/**
 * Update course basic information
 */
export const updateCourseBasicInfo = async (
  courseId: number,
  data: {
    title?: string;
    shortDescription?: string;
    description?: string;
    category?: number;
    subcategory?: number;
    level?: string;
    language?: number;
    courseDuration?: string;
    batchIds?: number[];
    availableToAllBatches?: boolean;
  }
): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<any>>(
      ENDPOINTS.UPDATE_BASIC_INFO(courseId),
      data
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update basic info',
      },
    };
  }
};

/**
 * Update course details (outcomes, requirements, FAQs)
 */
export const updateCourseDetails = async (
  courseId: number,
  data: {
    outcomes?: string[];
    requirements?: string[];
    faqs?: FAQ[];
  }
): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<any>>(
      ENDPOINTS.UPDATE_DETAILS(courseId),
      data
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update course details',
      },
    };
  }
};

/**
 * Update course media (thumbnail, overview video)
 */
export const updateCourseMedia = async (
  courseId: number,
  data: {
    mediaType?: string;
    mediaUrl?: string | File;
  }
): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<any>>(
      ENDPOINTS.UPDATE_MEDIA(courseId),
      data
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update course media',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Sections and Lessons
// ============================================================================

/**
 * Add a section to a course
 */
export const addSection = async (
  courseId: number,
  sectionTitle: string
): Promise<{
  success: boolean;
  data?: { sectionId: number };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ sectionId: number }>>(
      ENDPOINTS.ADD_SECTION(courseId),
      { title: sectionTitle }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to add section',
      },
    };
  }
};

/**
 * Get sections for a course
 */
export const getSections = async (courseId: number): Promise<{
  success: boolean;
  data?: Section[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Section[]>>(
      ENDPOINTS.GET_SECTIONS(courseId)
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch sections',
      },
    };
  }
};

/**
 * Add a lesson to a course
 */
export const addLesson = async (
  courseId: number,
  lessonData: Lesson
): Promise<{
  success: boolean;
  data?: { id: number };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ id: number }>>(
      ENDPOINTS.ADD_LESSON(courseId),
      lessonData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to add lesson',
      },
    };
  }
};

/**
 * Update a lesson
 */
export const updateLesson = async (
  courseId: number,
  lessonId: number,
  lessonData: Partial<Lesson>
): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<any>>(
      ENDPOINTS.UPDATE_LESSON(courseId, lessonId),
      lessonData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update lesson',
      },
    };
  }
};

/**
 * Delete a lesson
 */
export const deleteLesson = async (
  courseId: number,
  lessonId: number
): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      ENDPOINTS.DELETE_LESSON(courseId, lessonId)
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete lesson',
      },
    };
  }
};

/**
 * Reorder lessons within a section
 */
export const reorderLessons = async (
  courseId: number,
  sectionId: number,
  lessonOrders: Array<{ lessonId: number; lessonOrder: number }>
): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.put<ApiResponse<any>>(
      ENDPOINTS.REORDER_LESSONS(courseId),
      { sectionId, lessonOrders }
    );

    return { success: true };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to reorder lessons',
      },
    };
  }
};

/**
 * Get lesson details by ID
 * @param courseId - ID of the course
 * @param lessonId - ID of the lesson
 */
export const getLessonDetails = async (
  courseId: number,
  lessonId: number
): Promise<{
  success: boolean;
  data?: LessonData;
  error?: { message: string };
}> => {
  try {
    const endpoint = ENDPOINTS.GET_LESSON_DETAILS(courseId, lessonId);
    console.log('[AdminCourseManagementService] Fetching lesson details from:', endpoint);

    const response = await httpClient.get<ApiResponse<LessonData>>(endpoint);
    console.log('[AdminCourseManagementService] Raw lesson API response:', response);

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    console.error('[AdminCourseManagementService] Error fetching lesson details:', error);
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch lesson details',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Course Lists
// ============================================================================

/**
 * Get instructor's active (published) courses
 */
export const getActiveCourses = async (): Promise<{
  success: boolean;
  data?: CourseListItem[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CourseListItem[]>>(
      ENDPOINTS.GET_ACTIVE_COURSES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch active courses',
      },
    };
  }
};

/**
 * Get instructor's pending (draft) courses
 */
export const getPendingCourses = async (): Promise<{
  success: boolean;
  data?: CourseListItem[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CourseListItem[]>>(
      ENDPOINTS.GET_PENDING_COURSES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch pending courses',
      },
    };
  }
};

/**
 * Get all courses (is_deleted = 0, regardless of status)
 */
export const getAllCourses = async (): Promise<{
  success: boolean;
  data?: CourseListItem[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CourseListItem[]>>(
      ENDPOINTS.GET_ALL_COURSES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch all courses',
      },
    };
  }
};

/**
 * Get course statistics for admin dashboard cards
 */
export const getCourseStats = async (): Promise<{
  success: boolean;
  data?: CourseStats;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CourseStats>>(
      ENDPOINTS.GET_COURSE_STATS
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch course stats',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Metadata
// ============================================================================

/**
 * Get all course categories
 */
export const getCategories = async (): Promise<{
  success: boolean;
  data?: Category[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Category[]>>(
      ENDPOINTS.GET_CATEGORIES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch categories',
      },
    };
  }
};

/**
 * Get all course subcategories
 */
export const getSubCategories = async (): Promise<{
  success: boolean;
  data?: SubCategory[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<SubCategory[]>>(
      ENDPOINTS.GET_SUBCATEGORIES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch subcategories',
      },
    };
  }
};

/**
 * Get all languages
 */
export const getLanguages = async (): Promise<{
  success: boolean;
  data?: Language[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Language[]>>(
      ENDPOINTS.GET_LANGUAGES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch languages',
      },
    };
  }
};

/**
 * Get all instructors (role_id = 2)
 */
export const getInstructors = async (): Promise<{
  success: boolean;
  data?: Instructor[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Instructor[]>>(
      ENDPOINTS.GET_INSTRUCTORS
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch instructors',
      },
    };
  }
};

/**
 * Add a new category
 */
export const addCategory = async (categoryName: string): Promise<{
  success: boolean;
  data?: Category;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<Category>>(
      ENDPOINTS.ADD_CATEGORY,
      { categoryName }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to add category',
      },
    };
  }
};

/**
 * Add a new subcategory
 */
export const addSubCategory = async (
  categoryId: number,
  subcategoryName: string
): Promise<{
  success: boolean;
  data?: SubCategory;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<SubCategory>>(
      ENDPOINTS.ADD_SUBCATEGORY,
      { categoryId, subcategoryName }
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to add subcategory',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Batches
// ============================================================================

/**
 * Get all batches
 */
export const getBatches = async (): Promise<{
  success: boolean;
  data?: Batch[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Batch[]>>(
      ENDPOINTS.GET_BATCHES
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch batches',
      },
    };
  }
};

// ============================================================================
// SERVICE FUNCTIONS - Enrollments and Analytics
// ============================================================================

/**
 * Get enrolled students for a course
 */
export const getEnrolledStudents = async (courseId: number): Promise<{
  success: boolean;
  data?: any[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<any[]>>(
      ENDPOINTS.GET_ENROLLED_STUDENTS(courseId)
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch enrolled students',
      },
    };
  }
};

/**
 * Get student progress for a course
 */
export const getStudentProgress = async (courseId: number): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<any>>(
      ENDPOINTS.GET_STUDENT_PROGRESS(courseId)
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch student progress',
      },
    };
  }
};

/**
 * Get course analytics
 */
export const getCourseAnalytics = async (
  courseId: number,
  timeFilter?: '7days' | '30days' | '6months' | 'all'
): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const url = timeFilter
      ? `${ENDPOINTS.GET_COURSE_ANALYTICS(courseId)}?timeFilter=${timeFilter}`
      : ENDPOINTS.GET_COURSE_ANALYTICS(courseId);

    const response = await httpClient.get<ApiResponse<any>>(url);

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch course analytics',
      },
    };
  }
};

// ============================================================================
// COURSE OFFERINGS & SESSIONS
// ============================================================================

/**
 * Course offering interface
 */
export interface CourseOffering {
  id?: number;
  courseId?: number;
  name: string;
  locationId?: number | null;
  locationName?: string | null;
  location?: string;
  startDate: string;
  endDate: string;
  timezone?: string;
  totalSeats: number;
  availableSeats?: number;
  enableWaitlist: boolean;
  status?: 'Draft' | 'Published' | 'In Progress' | 'Completed' | 'Cancelled';
  sessionCount?: number;
  createdAt?: string;
}

/**
 * Course session interface
 */
export interface CourseSession {
  id?: number;
  courseId?: number;
  offeringId?: number | null;
  sectionId?: number | null;
  lessonId?: number | null;
  name: string;
  sessionDate: string;
  startTime: string;
  endTime: string;
  instructorId?: string | null;
  instructorName?: string;
  deliveryMethod: 'In-Person' | 'Virtual' | 'Hybrid';
  locationId?: number | null;
  roomId?: number | null;
  location?: string;
  roomName?: string;
  locationRoom?: string | null;
  virtualMeetingLink?: string | null;
  maxCapacity?: number;
  enrolledCount?: number;
  description?: string | null;
  status?: 'Scheduled' | 'In Progress' | 'Completed' | 'Cancelled';
  createdAt?: string;
}

// Add endpoints for offerings/sessions (using admin endpoints)
const OFFERINGS_SESSIONS_ENDPOINTS = {
  // Offerings
  CREATE_OFFERING: (courseId: number) => `/admin/courses/${courseId}/offerings`,
  GET_OFFERINGS: (courseId: number) => `/admin/courses/${courseId}/offerings`,
  UPDATE_OFFERING: (offeringId: number) => `/admin/courses/offerings/${offeringId}`,
  DELETE_OFFERING: (offeringId: number) => `/admin/courses/offerings/${offeringId}`,

  // Sessions
  CREATE_SESSION: (courseId: number) => `/admin/courses/${courseId}/sessions`,
  GET_SESSIONS: (courseId: number) => `/admin/courses/${courseId}/sessions`,
  UPDATE_SESSION: (sessionId: number) => `/admin/courses/sessions/${sessionId}`,
  DELETE_SESSION: (sessionId: number) => `/admin/courses/sessions/${sessionId}`,
};

/**
 * Create a course offering
 */
export const createCourseOffering = async (
  courseId: number,
  offeringData: CourseOffering
): Promise<{
  success: boolean;
  data?: { id: number; message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ id: number; message: string }>>(
      OFFERINGS_SESSIONS_ENDPOINTS.CREATE_OFFERING(courseId),
      offeringData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to create offering',
      },
    };
  }
};

/**
 * Get course offerings
 */
export const getCourseOfferings = async (courseId: number): Promise<{
  success: boolean;
  data?: CourseOffering[];
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.get<ApiResponse<CourseOffering[]>>(
      OFFERINGS_SESSIONS_ENDPOINTS.GET_OFFERINGS(courseId)
    );

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch offerings',
      },
    };
  }
};

/**
 * Update a course offering
 */
export const updateCourseOffering = async (
  offeringId: number,
  offeringData: Partial<CourseOffering>
): Promise<{
  success: boolean;
  data?: { message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<{ message: string }>>(
      OFFERINGS_SESSIONS_ENDPOINTS.UPDATE_OFFERING(offeringId),
      offeringData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update offering',
      },
    };
  }
};

/**
 * Delete a course offering
 */
export const deleteCourseOffering = async (offeringId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      OFFERINGS_SESSIONS_ENDPOINTS.DELETE_OFFERING(offeringId)
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete offering',
      },
    };
  }
};

/**
 * Create a course session
 */
export const createCourseSession = async (
  courseId: number,
  sessionData: CourseSession
): Promise<{
  success: boolean;
  data?: { id: number; message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ id: number; message: string }>>(
      OFFERINGS_SESSIONS_ENDPOINTS.CREATE_SESSION(courseId),
      sessionData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to create session',
      },
    };
  }
};

/**
 * Get course sessions
 */
export const getCourseSessions = async (
  courseId: number,
  offeringId?: number
): Promise<{
  success: boolean;
  data?: CourseSession[];
  error?: { message: string };
}> => {
  try {
    const url = offeringId
      ? `${OFFERINGS_SESSIONS_ENDPOINTS.GET_SESSIONS(courseId)}?offeringId=${offeringId}`
      : OFFERINGS_SESSIONS_ENDPOINTS.GET_SESSIONS(courseId);

    const response = await httpClient.get<ApiResponse<CourseSession[]>>(url);

    return {
      success: true,
      data: response.data || [],
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to fetch sessions',
      },
    };
  }
};

/**
 * Update a course session
 */
export const updateCourseSession = async (
  sessionId: number,
  sessionData: Partial<CourseSession>
): Promise<{
  success: boolean;
  data?: { message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.put<ApiResponse<{ message: string }>>(
      OFFERINGS_SESSIONS_ENDPOINTS.UPDATE_SESSION(sessionId),
      sessionData
    );

    return {
      success: true,
      data: response.data,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update session',
      },
    };
  }
};

/**
 * Delete a course session
 */
export const deleteCourseSession = async (sessionId: number): Promise<{
  success: boolean;
  error?: { message: string };
}> => {
  try {
    await httpClient.delete<ApiResponse<null>>(
      OFFERINGS_SESSIONS_ENDPOINTS.DELETE_SESSION(sessionId)
    );

    return {
      success: true,
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to delete session',
      },
    };
  }
};

// ============================================================================
// ADMIN-SPECIFIC OPERATIONS
// ============================================================================

/**
 * Approve a course (admin only)
 * @param courseId - ID of the course to approve
 */
export const approveCourse = async (courseId: number): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.patch<ApiResponse<any>>(
      ENDPOINTS.APPROVE_COURSE(courseId)
    );
    return { success: true, data: response.data, error: undefined };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error.message || 'Failed to approve course' }
    };
  }
};

/**
 * Reject a course (admin only)
 * @param courseId - ID of the course to reject
 */
export const rejectCourse = async (courseId: number): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.patch<ApiResponse<any>>(
      ENDPOINTS.REJECT_COURSE(courseId)
    );
    return { success: true, data: response.data, error: undefined };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error.response?.data?.message || error.message || 'Failed to reject course' }
    };
  }
};

/**
 * Update course status to active, inactive, or pending
 * @param courseId - ID of the course
 * @param status - New status ('active', 'inactive', or 'pending')
 */
export const updateCourseStatus = async (
  courseId: number,
  status: 'active' | 'inactive' | 'pending'
): Promise<{
  success: boolean;
  data?: { courseId: number; status: string; message: string };
  error?: { message: string };
}> => {
  try {
    const response = await httpClient.patch<ApiResponse<any>>(
      ENDPOINTS.UPDATE_COURSE_STATUS(courseId),
      { status }
    );

    return {
      success: true,
      data: {
        courseId,
        status,
        message: response.message || `Course status updated to ${status} successfully`
      },
      error: undefined,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        message: error.response?.data?.message || error.message || 'Failed to update course status'
      }
    };
  }
};
